import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  WORD_MASTER_FIELD_LABELS,
  WORD_MASTER_FIELD_ORDER,
  emptyWordMasterMapping,
  guessWordMasterMapping,
  type WordMasterColumnMapping,
} from "../wordMasterExcel";

const MAP_NONE = "__wm_none__";

interface WordMasterImportMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  excelColumns: string[];
  /** Number of data rows under those headers (same as SheetJS row count). */
  rawRowCount: number;
  onConfirm: (mapping: WordMasterColumnMapping) => void;
}

export function WordMasterImportMappingDialog({
  open,
  onOpenChange,
  excelColumns,
  rawRowCount,
  onConfirm,
}: WordMasterImportMappingDialogProps) {
  const [mapping, setMapping] =
    useState<WordMasterColumnMapping>(emptyWordMasterMapping);

  useEffect(() => {
    if (open && excelColumns.length > 0) {
      setMapping(guessWordMasterMapping(excelColumns));
    }
    if (!open) {
      setMapping(emptyWordMasterMapping());
    }
  }, [open, excelColumns]);

  const canImport = mapping.word != null && mapping.word !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Map spreadsheet columns</DialogTitle>
          
        </DialogHeader>

        <div className="border-border/60 my-4 flex max-h-[min(52vh,520px)] flex-col gap-3 overflow-y-auto border-y py-4 pr-1">
          {WORD_MASTER_FIELD_ORDER.map(field => {
            const meta = WORD_MASTER_FIELD_LABELS[field];
            const value = mapping[field];
            return (
              <div
                key={field}
                className="grid items-start gap-2 sm:grid-cols-[minmax(160px,44%)_1fr] sm:items-center"
              >
                <Label className="text-muted-foreground pt-2 text-sm sm:pt-0">
                  {meta.label}
                  {meta.required ? (
                    <span className="text-destructive"> *</span>
                  ) : null}
                </Label>
                <Select
                  value={value ?? MAP_NONE}
                  onValueChange={next =>
                    setMapping(prev => ({
                      ...prev,
                      [field]: next === MAP_NONE ? null : next,
                    }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Not mapped" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-72">
                    <SelectItem value={MAP_NONE}>Not mapped</SelectItem>
                    {excelColumns.map(col => (
                      <SelectItem key={col} value={col}>
                        {col}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canImport}
            className="bg-primary hover:bg-primary/90"
            onClick={() => {
              if (!canImport) return;
              onConfirm(mapping);
            }}
          >
            Import with this mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
